// React
import { useEffect, useMemo, useState } from "react";

// MUI
import { Box, Grid, Skeleton, Typography } from "@mui/material";

import { Bar, BarChart, XAxis, YAxis } from "recharts";

// Custom styles
import "./GradientBarGraph.css";

// Images
import graphGradient from "@/assets/icons/dataPages/cit/citGradient.svg";
import { AnimatedNumber } from "../AnimatedNumber";

const NUMBER_OF_Y_TICKS_STEPS = 5;
const BOUNDARY_NUMBER = 25;

interface ITickPropsArgs {
	x: number;
	y: number;
	payload: { value: number; offset: number };
	index: number;
}

export interface IAnalyticsData {
	age: string;
	quantity: number;
	amount: string;
	exactAmount: number;
	x?: number;
	width?: number;
}

interface IGradientBarGraphProps {
	data: IAnalyticsData[] | null;
	xAxis: string;
	xAxisLabel: string;
	yAxisLabel: string;
	dataKey: "amount" | "quantity";
	maxY: number;
	title?: string;
	isLoading: boolean;
	postfix?: string;
	symbol?: string;
	decimals?: number;
}

export default function GradientBarGraph(props: Readonly<IGradientBarGraphProps>) {
	const [xIndex, setXIndex] = useState<number | null>(null);
	const [yIndex, setYIndex] = useState<number | null>(null);
	const indexLineWidth = 0;
	const [yTicks, setYTicks] = useState<number[]>([]);
	const [defaultTicks, setDefaultTicks] = useState<number[]>([]);

	const uniqueGradientId = useMemo(() => `fillPattern-${Math.random().toString(36).substring(2, 9)}`, []);

	const tickColor = "#2357be";

	const ageTick = (tickProps: ITickPropsArgs) => {
		const { x, y, payload, index } = tickProps;
		const { value, offset } = payload;

		if (xIndex && (index === xIndex || index === xIndex - 1)) {
			return (
				<>
					<path d={`M${x + offset},${y - 2}v${-6}`} stroke={tickColor} strokeWidth={2} />
					<text x={x + offset} y={y + 10} textAnchor="middle" stroke={tickColor} fontSize="14px">
						{`${value}`}
					</text>
				</>
			);
		}

		return (
			<>
				<path d={`M${x + offset},${y - 2}v${-6}`} stroke="#666666" />
				<text x={x + offset} y={y + 6} textAnchor="middle" fill="#666666">
					{`${value}`}
				</text>
			</>
		);
	};

	const yTick = (tickProps: ITickPropsArgs) => {
		const { x, y, payload, index } = tickProps;
		const { value } = payload;
		const symbol = props.symbol ? props.symbol + " " : "";
		const postfix = props.postfix ? " " + props.postfix : "";
		const decimals = props?.decimals ?? 0;
		const decimalValue = Number(value).toFixed(decimals);

		return (
			<>
				{index === yIndex ? (
					<g>
						<path d={`M ${x + 8},${y} h${indexLineWidth}`} stroke="#666666" strokeDasharray="4 2" />
						<path d={`M ${x + 2},${y} h${6}`} stroke="#666666" strokeWidth={2} />
						<text x={x} y={y + 3} textAnchor="end" stroke="#666666" fontSize="12px">
							{`${symbol}${decimalValue}${postfix}`}
						</text>
					</g>
				) : (
					<g>
						<path d={`M ${x + 2},${y} h${6}`} stroke="#666666" />
						<text x={x} y={y + 3} textAnchor="end" fill="#666666">
							{`${symbol}${decimalValue}${postfix}`}
						</text>
					</g>
				)}
				{!index && !xIndex && (
					<>
						<path d={`M ${x + 8},${270} v${6}`} stroke={tickColor} strokeWidth={2} />
						<text x={x + 8} y={288} textAnchor="middle" stroke={tickColor} fontSize="12px">
							0
						</text>
					</>
				)}
				{!index && xIndex && (
					<>
						<path d={`M ${x + 8},${270} v${6}`} stroke="#666666" />
						<text x={x + 8} y={285} textAnchor="middle" fill="#666666">
							0
						</text>
					</>
				)}
			</>
		);
	};

	const handleIn = (data: IAnalyticsData, index: number) => {
		const step = props.maxY / NUMBER_OF_Y_TICKS_STEPS;
		const boundary = props.maxY / BOUNDARY_NUMBER;
		const value = Number(data[props.dataKey]);

		if (String(data.age) === "") {
			return;
		}

		let steps = [value];
		for (let i = 0; i <= props.maxY; i += step) {
			if (i + boundary < value || i - boundary > value) {
				steps.push((i * 100) / 100);
			}
		}
		steps = steps.sort((a, b) => a - b);
		setYIndex(steps.findIndex((entry) => entry === value));
		setYTicks(steps);
		setXIndex(index);
	};

	const handleOut = () => {
		setYTicks(defaultTicks);
		setXIndex(null);
		setYIndex(null);
	};

	const accumulateAnalyticValues = (data: IAnalyticsData[], prop: "quantity" | "amount") => {
		const property = prop === "amount" ? "exactAmount" : prop;
		return data.reduce((acc, cur) => acc + Number(cur?.[property]), 0);
	};

	useEffect(() => {
		if (props.data) {
			const step = props.maxY / NUMBER_OF_Y_TICKS_STEPS;
			const ticks = Array.from({ length: 6 }, (_, i) => i * step);

			setYTicks(ticks);
			setDefaultTicks(ticks);
		}
	}, [props]);

	return (
		<>
			{props.data?.[0]?.age === "" && !props.isLoading ? (
				<Typography className={"no-data-to-analyze"}>No Data to Analyze</Typography>
			) : (
				""
			)}
			<Grid item xs={12} className="gradient-bar-graph">
				{props.isLoading ? (
					<>
						<Skeleton width={200} height={42} />
						<Skeleton variant="rectangular" height={300} width={600} sx={{ borderRadius: "6px" }} />
					</>
				) : (
					<Grid container direction="column">
						<Box className="!relative h-full w-full">
							<Typography
								className={`graphGradientLabel yLabel ${!props.postfix && "noPostFixLabel"}`}
								sx={{ width: "100%", textAlign: "center" }}
							>
								{props.yAxisLabel}
							</Typography>
							<Typography className="graphGradientLabel xLabel">{props.xAxisLabel}</Typography>
						</Box>
						{props.title && (
							<Typography
								sx={{
									fontFamily: "var(--font-family-v1)",
									color: "var(--text-color)",
									paddingLeft: "70px",
									fontSize: "25px",
									fontWeight: 500,
								}}
								component="span"
							>
								{props.title}: {props.title === "Unfunded Dollars" ? <sup>$</sup> : ""}
								<AnimatedNumber finalValue={accumulateAnalyticValues(props?.data ?? [], props.dataKey)} />
							</Typography>
						)}
						<BarChart
							width={600}
							height={300}
							data={props.data ?? []}
							barCategoryGap={1}
							margin={{
								top: 15,
								right: 10,
								left: 0,
								bottom: 0,
							}}
						>
							<defs>
								<pattern id={uniqueGradientId} patternUnits="userSpaceOnUse" width="600" height="300">
									<image xlinkHref={graphGradient} x="0" y="0" width="600" height="300" />
								</pattern>
							</defs>
							<XAxis dataKey={props.xAxis} tick={ageTick} tickLine={false} padding={{ left: 1 }} />
							<Bar
								dataKey={props.dataKey}
								radius={[10, 10, 0, 0]}
								fill={`url('#${uniqueGradientId}')`}
								onMouseEnter={handleIn}
								onMouseOut={handleOut}
								background={{ fill: "transparent" }}
							/>
							<YAxis domain={[0, props.maxY]} allowDecimals={false} tick={yTick} ticks={yTicks} interval={0} width={70} />
						</BarChart>
					</Grid>
				)}
			</Grid>
		</>
	);
}
